// Generated from scripts/facets.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DrawerCloseEvent, ThemeDrawer } from '@theme/theme-drawer';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { clamp, debounce, mediaQueryLarge, startViewTransition } from '@theme/utilities';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/events';
const SECTION_ID_PREFIX = 'shopify-section-';
const RESULTS_SELECTOR = '#ResultsList';
/** Morph skips the children of a node that carries this attribute on both its live and incoming copy. */
const FROZEN_ATTRIBUTE = 'data-skip-subtree-update';
const SORT_PARAMETER = 'sort_by';
const PAGE_PARAMETER = 'page';
const PRICE_PARAMETERS = ['filter.v.price.gte', 'filter.v.price.lte'];
/** The price controls ask for an update only after the shopper pauses this long. */
const PRICE_DEBOUNCE_MS = 300;
/** A live region only announces a change, so it is emptied and refilled after this pause. */
const ANNOUNCE_DELAY_MS = 100;
/** The slider offers about this many positions, whatever the price range. */
const SLIDER_POSITIONS = 100;
/** Filter and sorting parameters belong to this module; everything else in the address is the page's own. */
function isFacetParameter(name) {
    return name.startsWith('filter.') || name === SORT_PARAMETER;
}
/** Two addresses with the same key show the same results; the page position does not count. */
function stateKey(url) {
    const parameters = new URLSearchParams(url.searchParams);
    parameters.delete(PAGE_PARAMETER);
    parameters.sort();
    return `${url.pathname}?${parameters}`;
}
/** An amount as Shopify's filter parameters expect it: plain digits, a point, no grouping. */
function plainAmount(minorUnits, currency) {
    return formatMoney(minorUnits, '{{amount}}', currency).replaceAll(',', '');
}
/**
 * Renders the filtered results in place.
 *
 * The server renders every filter state as a real page: the filters are a GET form, the active filters
 * are links and the results are plain markup. This element turns those into in-place updates. Each change
 * makes one Section Rendering API request for the section that holds the results, the filter forms, the
 * active filters and the count, then morphs the response into the page.
 *
 * A request that a newer one replaces is aborted, and a generation counter ignores any answer that still
 * arrives late, so an old answer never replaces a newer grid. The address is written when the answer
 * lands, so one burst of changes leaves one history entry, and the back button restores the previous
 * state. While the filter drawer is open the grid behind it is left alone and updates once the drawer
 * closes.
 */
export class FacetsFormComponent extends Component {
    #lifetime;
    #request;
    #generation = 0;
    #inFlight = false;
    #settlers = [];
    #pending;
    #stableUrl = new URL(location.href);
    #committedKey = stateKey(this.#stableUrl);
    #announceTimer;
    #sidebarHidden = false;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = new AbortController();
        const { signal } = this.#lifetime;
        this.#stableUrl = new URL(location.href);
        this.#committedKey = stateKey(this.#stableUrl);
        this.addEventListener('change', this.#onChange, { signal });
        this.addEventListener('submit', this.#onSubmit, { signal });
        this.addEventListener('click', this.#onClick, { signal });
        this.addEventListener('keydown', this.#onKeydown, { signal });
        // The toggle event does not bubble, so the capture phase is the one place an ancestor sees it.
        this.addEventListener('toggle', this.#onToggle, { capture: true, signal });
        this.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose, { signal });
        document.addEventListener('pointerdown', this.#onPointerDown, { signal });
        window.addEventListener('popstate', this.#onPopState, { signal });
        // In the sidebar layout the in-page filters take over from the drawer at the large breakpoint.
        if (this.dataset.filterStyle === 'vertical')
            mediaQueryLarge.addEventListener('change', this.#onBreakpoint, { signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        // Raising the generation makes any answer still on its way a stale one.
        this.#generation += 1;
        this.#request?.abort();
        this.#inFlight = false;
        this.#pending = undefined;
        window.clearTimeout(this.#announceTimer);
        this.#settle();
    }
    get sectionId() {
        const id = this.getAttribute('section-id');
        if (!id)
            throw new Error('The section-id attribute is required');
        return id;
    }
    /** Server-confirmed controls may be ahead of the address while the mobile drawer holds its result update. */
    get filterURL() { return new URL(this.#stableUrl); }
    get #inlineForm() {
        return this.querySelector('form[data-facets-form="inline"]');
    }
    get #drawer() {
        const drawer = this.querySelector('theme-drawer');
        return drawer instanceof ThemeDrawer ? drawer : null;
    }
    get #drawerIsOpen() {
        return this.#drawer?.isOpen === true;
    }
    get #priceComponents() {
        return [...this.querySelectorAll('price-facet-component')].filter((element) => element instanceof PriceFacetComponent);
    }
    /** The filter and sorting parameters a form holds, in the form Shopify's addresses use. */
    createURLParameters(formData) {
        const parameters = new URLSearchParams();
        for (const [name, value] of formData) {
            if (typeof value === 'string' && isFacetParameter(name))
                parameters.append(name, value);
        }
        const currency = this.dataset.currency ?? '';
        for (const name of PRICE_PARAMETERS) {
            const raw = parameters.get(name);
            if (raw === null)
                continue;
            // The field shows the shop's money format; the address carries plain digits. An empty or unreadable field means no bound.
            const minorUnits = convertMoneyToMinorUnits(raw, currency);
            parameters.delete(name);
            if (minorUnits !== null)
                parameters.set(name, plainAmount(minorUnits, currency));
        }
        if (parameters.get(SORT_PARAMETER) === this.dataset.defaultSortBy)
            parameters.delete(SORT_PARAMETER);
        return parameters;
    }
    /** Applies the filters and sorting that a form holds; without one, the inline form. */
    updateFilters(form = this.#inlineForm) {
        if (!form)
            return;
        // The form already carries the price fields, so a price update still waiting would only repeat this one.
        for (const price of this.#priceComponents)
            if (form.contains(price))
                price.discardPending();
        void this.#load(this.#targetUrl(this.createURLParameters(new FormData(form))), { push: true });
    }
    /** Applies the filters of an address, such as the one an active filter links to. */
    updateFiltersByURL(address) {
        const source = new URL(address, location.href);
        void this.#load(this.#targetUrl(source.searchParams), { push: true });
    }
    /** The page's own address with its filter and sorting parameters replaced. */
    #targetUrl(facetParameters) {
        const url = new URL(location.href);
        url.hash = '';
        for (const name of [...url.searchParams.keys()]) {
            if (isFacetParameter(name) || name === PAGE_PARAMETER)
                url.searchParams.delete(name);
        }
        for (const [name, value] of facetParameters) {
            if (isFacetParameter(name))
                url.searchParams.append(name, value);
        }
        return url;
    }
    async #load(url, { push }) {
        const generation = (this.#generation += 1);
        this.#request?.abort();
        const controller = new AbortController();
        this.#request = controller;
        this.#inFlight = true;
        this.#setBusy(true);
        this.#showError(false);
        this.#publishUpdate(url);
        let html;
        try {
            html = await this.#fetchSection(url, controller.signal);
        }
        catch (error) {
            if (generation === this.#generation)
                this.#fail(error);
            return;
        }
        if (generation !== this.#generation)
            return;
        // A price change still waiting makes this answer stale before it lands; its own request replaces it.
        if (this.#priceComponents.some((price) => price.pending))
            return;
        this.#inFlight = false;
        this.#pending = undefined;
        this.#stableUrl = url;
        try {
            if (push && this.#drawerIsOpen) {
                // The drawer shows the effect of each change in its own filters and count; the grid follows when it closes.
                this.#withFocus(() => this.#applySection(html, true));
                this.#pending = { url, html };
                this.#complete(true);
            }
            else {
                await this.#commit(html, url, { push, animate: true, announce: true });
            }
        }
        catch (error) {
            if (generation === this.#generation)
                this.#fail(error);
        }
    }
    /** One request returns the results, the filter forms, the active filters and the count. */
    async #fetchSection(url, signal) {
        const requestUrl = new URL(url);
        requestUrl.searchParams.set('sections', this.sectionId);
        const response = await fetch(requestUrl, { signal });
        if (!response.ok)
            throw new Error(`The results request failed with status ${response.status}`);
        const payload = await response.json();
        // A section the template does not render comes back as null in a successful response.
        const html = typeof payload === 'object' && payload !== null ? payload[this.sectionId] : null;
        if (typeof html !== 'string')
            throw new Error('The results request returned no markup for the section');
        return html;
    }
    #parse(html) {
        const incoming = new DOMParser().parseFromString(html, 'text/html').getElementById(`${SECTION_ID_PREFIX}${this.sectionId}`);
        if (!incoming)
            throw new Error('The results request returned markup without the section');
        return incoming;
    }
    /** Morphs the section into the page; with `freezeResults` the grid and its pagination stay as they are. */
    #applySection(html, freezeResults) {
        const incoming = this.#parse(html);
        const live = document.getElementById(`${SECTION_ID_PREFIX}${this.sectionId}`);
        if (!live)
            throw new Error('The section is no longer on the page');
        const liveResults = freezeResults ? live.querySelector(RESULTS_SELECTOR) : null;
        const incomingResults = freezeResults ? incoming.querySelector(RESULTS_SELECTOR) : null;
        if (liveResults && incomingResults) {
            liveResults.setAttribute(FROZEN_ATTRIBUTE, '');
            incomingResults.setAttribute(FROZEN_ATTRIBUTE, '');
        }
        morph(live, incoming);
        liveResults?.removeAttribute(FROZEN_ATTRIBUTE);
        // The incoming markup shows the sidebar; the shopper's choice is put back before the page paints.
        this.#syncSidebar();
        // The morph hands updatedCallback the incoming copy of an element, which is parsed outside the document and
        // is not an upgraded component. The price sliders and the paginated list depend on that call, so it is made here.
        for (const price of this.#priceComponents)
            price.refresh();
        const list = freezeResults ? null : this.closest('results-list');
        if (list instanceof Component)
            list.updatedCallback();
    }
    /** Writes the address and the new results together. */
    async #commit(html, url, { push, animate, announce }) {
        const generation = this.#generation;
        const apply = () => {
            if (generation !== this.#generation)
                return;
            if (push)
                history.pushState(null, '', url);
            this.#withFocus(() => this.#applySection(html, false));
            // The page shows this state from here on, while the view transition below is still playing. Back pressed in that
            // time must compare with this state and not with the one before it, or it would find the address unchanged and
            // leave the grid on the results it is leaving.
            this.#committedKey = stateKey(url);
        };
        if (animate && !this.#drawerIsOpen)
            await startViewTransition(apply, ['product-grid']);
        else
            apply();
        if (generation !== this.#generation)
            return;
        this.#complete(announce);
    }
    #complete(announce) {
        this.#setBusy(false);
        if (announce)
            this.#announce(this.#countText());
        this.#settle(this.#resultsCount());
    }
    #fail(error) {
        this.#inFlight = false;
        this.#restoreControls(this.#stableUrl.searchParams);
        this.#setBusy(false);
        this.#showError(true);
        for (const settler of this.#settlers.splice(0))
            settler.reject(error);
    }
    /** Resolves the standard-event promises of every update that this answer covers. */
    #settle(count = this.#resultsCount()) {
        for (const settler of this.#settlers.splice(0))
            settler.resolve(count);
    }
    /**
     * Tells analytics listeners that the results are changing, as the standard events define. `detail.surface` tells the
     * analytics bridge that the shopper changed the filters or the sorting, as opposed to the instant search results.
     */
    #publishUpdate(url) {
        const parameters = new URLSearchParams(url.search);
        if (this.dataset.pageType === 'search') {
            const deferred = SearchUpdateEvent.createPromise();
            const productFilters = SearchUpdateEvent.parseProductFilters(parameters);
            this.dispatchEvent(new SearchUpdateEvent({
                search: {
                    query: parameters.get('q') ?? '',
                    ...(productFilters && { productFilters }),
                    sortKey: SearchUpdateEvent.getSortKey(parameters),
                },
                promise: deferred.promise,
                detail: { surface: 'facets' },
            }));
            this.#settlers.push({ resolve: (count) => deferred.resolve({ totalCount: count }), reject: deferred.reject });
            return;
        }
        const deferred = CollectionUpdateEvent.createPromise();
        const productFilters = CollectionUpdateEvent.parseProductFilters(parameters);
        // Only numeric ids are valid global ids; a collection such as "all" has none.
        const rawId = this.dataset.collectionId;
        this.dispatchEvent(new CollectionUpdateEvent({
            collection: {
                id: rawId && /^\d+$/.test(rawId) ? rawId : null,
                handle: this.dataset.collectionHandle ?? '',
                productsCount: this.#resultsCount(),
            },
            ...(productFilters && { productFilters }),
            sortKey: CollectionUpdateEvent.getSortKey(parameters),
            promise: deferred.promise,
            detail: { surface: 'facets' },
        }));
        this.#settlers.push({ resolve: (count) => deferred.resolve({ productsCount: count }), reject: deferred.reject });
    }
    #resultsCount() {
        return Number(this.dataset.resultsCount) || 0;
    }
    #countText() {
        return this.querySelector('[data-facets-count]')?.textContent?.trim() ?? '';
    }
    /** Dims the results while a request is out, and marks the region busy for assistive technology. */
    #setBusy(busy) {
        this.toggleAttribute('data-busy', busy);
        const results = this.closest('results-list')?.querySelector(RESULTS_SELECTOR);
        if (busy)
            results?.setAttribute('aria-busy', 'true');
        else
            results?.removeAttribute('aria-busy');
    }
    #showError(visible) {
        const messages = this.querySelectorAll('[data-facets-error]');
        for (const message of messages)
            message.hidden = !visible;
        if (visible)
            this.#announce(messages[0]?.textContent?.trim() ?? '');
    }
    /** One polite message per update: the page's live region, or the drawer's while the drawer is open. */
    #announce(message) {
        window.clearTimeout(this.#announceTimer);
        const region = this.querySelector(`[data-facets-live="${this.#drawerIsOpen ? 'drawer' : 'page'}"]`);
        if (!region || !message)
            return;
        region.textContent = '';
        this.#announceTimer = window.setTimeout(() => {
            region.textContent = message;
        }, ANNOUNCE_DELAY_MS);
    }
    /** Puts the controls back to the state of an address, as when an update fails. */
    #restoreControls(parameters) {
        for (const form of this.querySelectorAll('form')) {
            for (const element of form.elements) {
                if (element instanceof HTMLInputElement && element.type === 'checkbox') {
                    element.checked = parameters.getAll(element.name).includes(element.value);
                }
                else if (element instanceof HTMLSelectElement && element.name === SORT_PARAMETER) {
                    element.value = parameters.get(SORT_PARAMETER) ?? this.dataset.defaultSortBy ?? element.value;
                }
            }
        }
        for (const price of this.#priceComponents)
            price.restore(parameters);
    }
    /** Keeps keyboard focus on the control the shopper used, even when the update replaces its node. */
    #withFocus(update) {
        const focused = this.#captureFocus();
        update();
        this.#restoreFocus(focused);
    }
    #captureFocus() {
        const active = document.activeElement;
        if (!(active instanceof HTMLElement) || !this.contains(active))
            return null;
        const chips = [...this.querySelectorAll('[data-facets-chip]')];
        const chip = active.closest('[data-facets-chip]');
        if (chip)
            return { kind: 'chip', index: chips.indexOf(chip) };
        // Clear all comes after the last chip, and leaves with them: it hands the focus on as a removed last chip does.
        if (active.closest('[data-facets-chips]'))
            return { kind: 'chip', index: chips.length };
        // The drawer's Clear all leaves when nothing is left to clear.
        if (active.matches('a[data-facets-link]') && this.#drawer?.contains(active))
            return { kind: 'drawer' };
        return active.id ? { kind: 'id', id: active.id } : null;
    }
    #restoreFocus(target) {
        if (!target)
            return;
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body && this.contains(active))
            return;
        if (target.kind === 'id') {
            document.getElementById(target.id)?.focus({ preventScroll: true });
            return;
        }
        if (target.kind === 'drawer') {
            // The button beside it applies the filters; with no results there is none, and the close button is next.
            const next = this.#drawer?.querySelector('button[type="submit"]') ?? this.#drawer?.querySelector('.theme-drawer__close-button');
            next?.focus({ preventScroll: true });
            return;
        }
        // A removed filter's neighbour takes the focus; with no filter left, the first control of the toolbar does.
        const chips = [...this.querySelectorAll('[data-facets-chip]')];
        // The first control that is on screen: the toolbar also holds the drawer's button on a computer and the inline
        // filters on a phone, which are there for the other size and cannot take the focus.
        const fallback = [...this.querySelectorAll('[data-facets-toolbar] :is(button, summary, select)')].find((control) => control.checkVisibility());
        (chips[Math.min(target.index, chips.length - 1)] ?? fallback)?.focus({ preventScroll: true });
    }
    #onChange = (event) => {
        const control = event.target;
        if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement))
            return;
        // The price controls wait for the shopper to pause before they ask for an update.
        if (control.closest('price-facet-component'))
            return;
        const form = control.form;
        if (form && this.contains(form))
            this.updateFilters(form);
    };
    #onSubmit = (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !this.contains(form))
            return;
        event.preventDefault();
        let flushed = false;
        for (const price of this.#priceComponents)
            if (price.flush())
                flushed = true;
        if (form.dataset.facetsForm === 'drawer') {
            // The drawer's button applies its changes by closing the drawer.
            void this.#drawer?.close();
        }
        else if (!flushed) {
            this.updateFilters(form);
        }
    };
    /**
     * Hides or shows the sidebar the shopper can hide. The choice lives here, not in the markup, so a filter update that
     * morphs the section keeps it; a page load shows the sidebar again. Focus moves to the control that takes over.
     */
    #toggleSidebar() {
        this.#sidebarHidden = !this.#sidebarHidden;
        this.#syncSidebar();
        const next = this.querySelector(this.#sidebarHidden ? '[data-facets-sidebar-open]' : '[data-facets-sidebar-close]');
        next?.focus({ preventScroll: true });
    }
    #syncSidebar() {
        this.toggleAttribute('data-sidebar-hidden', this.#sidebarHidden);
        for (const control of this.querySelectorAll('[data-facets-sidebar-toggle]')) {
            control.setAttribute('aria-expanded', String(!this.#sidebarHidden));
        }
    }
    #onClick = (event) => {
        if (event.target instanceof Element && event.target.closest('[data-facets-sidebar-toggle]')) {
            this.#toggleSidebar();
            return;
        }
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
            return;
        if (!(event.target instanceof Element))
            return;
        const link = event.target.closest('a[data-facets-link]');
        if (!link || !this.contains(link))
            return;
        event.preventDefault();
        this.updateFiltersByURL(link.href);
    };
    /** Applies what the drawer collected once it closes, however it was closed. */
    #onDrawerClose = () => {
        const pending = this.#pending;
        // A request still in flight will write its own answer.
        if (!pending || this.#inFlight)
            return;
        this.#pending = undefined;
        const generation = this.#generation;
        this.#commit(pending.html, pending.url, { push: true, animate: false, announce: false }).catch((error) => {
            if (generation === this.#generation)
                this.#fail(error);
        });
    };
    /** Back and forward: show the results of the address the browser returned to. */
    #onPopState = () => {
        const url = new URL(location.href);
        if (stateKey(url) === this.#committedKey)
            return;
        this.#pending = undefined;
        void this.#load(url, { push: false });
    };
    #onBreakpoint = (event) => {
        if (event.matches)
            void this.#drawer?.close();
    };
    /** One filter of the top bar is open at a time, and its panel stays inside the viewport. */
    #onToggle = (event) => {
        const details = event.target;
        if (!(details instanceof HTMLDetailsElement) || !details.open)
            return;
        const bar = details.closest('[data-facets-bar]');
        if (!bar)
            return;
        for (const other of bar.querySelectorAll('details[open]')) {
            if (other instanceof HTMLDetailsElement && other !== details)
                other.open = false;
        }
        const panel = details.querySelector('[data-facets-panel]');
        if (!panel)
            return;
        delete panel.dataset.align;
        const bounds = panel.getBoundingClientRect();
        if (bounds.right > document.documentElement.clientWidth)
            panel.dataset.align = 'right';
        else if (bounds.left < 0)
            panel.dataset.align = 'left';
    };
    #onPointerDown = (event) => {
        if (!(event.target instanceof Node))
            return;
        for (const details of this.querySelectorAll('[data-facets-bar] details[open]')) {
            if (details instanceof HTMLDetailsElement && !details.contains(event.target))
                details.open = false;
        }
    };
    #onKeydown = (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented || !(event.target instanceof Element))
            return;
        const details = event.target.closest('[data-facets-bar] details[open]');
        if (!(details instanceof HTMLDetailsElement))
            return;
        event.preventDefault();
        details.open = false;
        details.querySelector('summary')?.focus();
    };
}
/**
 * The price range: two native range inputs for the lowest and highest price, and two money fields, kept
 * in step. An empty field means no bound, and so does a thumb at its end. A change asks for an update only
 * after the shopper pauses, so dragging or typing makes one request.
 */
class PriceFacetComponent extends Component {
    #lifetime;
    #scheduled = false;
    #currency = 'USD';
    #fullFormat = '{{amount}}';
    #fieldFormat = '{{amount}}';
    /** The collection's highest price, in minor units. */
    #limit = 0;
    /** The range inputs run to this value, the first step multiple at or above the limit. */
    #ceiling = 0;
    #step = 1;
    /** The range the results already show, as `#key` writes it. */
    #applied = '';
    #commit = debounce(() => {
        this.#scheduled = false;
        this.#request();
    }, PRICE_DEBOUNCE_MS);
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = new AbortController();
        const { signal } = this.#lifetime;
        this.addEventListener('input', this.#onInput, { signal });
        this.addEventListener('change', this.#onChange, { signal });
        this.addEventListener('keydown', this.#onKeydown, { signal });
        this.addEventListener('beforeinput', this.#onBeforeInput, { signal });
        this.addEventListener('focusin', this.#onFocusIn, { signal });
        this.addEventListener('focusout', this.#onFocusOut, { signal });
        this.#configure();
        this.#adopt();
    }
    /** A section morph brings the server's values; the sliders follow the fields. */
    updatedCallback() {
        super.updatedCallback();
        this.refresh();
    }
    /** Reads the range and the bounds the server rendered and shows them as the range the results already show. */
    refresh() {
        this.#configure();
        this.#adopt();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.discardPending();
    }
    /** Whether a change is waiting for the shopper to pause. */
    get pending() {
        return this.#scheduled;
    }
    /** Drops the update that is waiting; another update has taken over. */
    discardPending() {
        this.#commit.cancel();
        this.#scheduled = false;
    }
    /** Asks for the update right away when one is waiting, and reports whether it did. */
    flush() {
        if (!this.#scheduled)
            return false;
        this.#request();
        return true;
    }
    /** Shows the bounds that an address holds. */
    restore(parameters) {
        this.discardPending();
        const [lowName, highName] = PRICE_PARAMETERS;
        this.#show(this.#parse(parameters.get(lowName) ?? '') ?? 0, this.#parse(parameters.get(highName) ?? '') ?? this.#limit);
        this.#applied = this.#key();
    }
    #field(kind) {
        return this.querySelector(`input[data-price-field="${kind}"]`);
    }
    #range(kind) {
        return this.querySelector(`input[data-price-range="${kind}"]`);
    }
    #configure() {
        this.#currency = this.dataset.currency || 'USD';
        this.#fullFormat = this.dataset.moneyFormat || '{{amount}}';
        // The fields show the amount alone; the currency symbol sits in front of them.
        this.#fieldFormat = this.#fullFormat.match(/{{\s*\w+\s*}}/)?.[0] ?? '{{amount}}';
        this.#limit = convertMoneyToMinorUnits(this.dataset.rangeMax ?? '', this.#currency) ?? 0;
        this.#step = this.#niceStep(this.#limit);
        this.#ceiling = Math.ceil(this.#limit / this.#step) * this.#step;
    }
    /** The smallest 1, 2 or 5 times a power of ten that leaves the slider about 100 positions. */
    #niceStep(limit) {
        const target = Math.max(1, limit / SLIDER_POSITIONS);
        const magnitude = 10 ** Math.floor(Math.log10(target));
        return [1, 2, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= target) ?? magnitude * 10;
    }
    #parse(text) {
        const minorUnits = convertMoneyToMinorUnits(text, this.#currency);
        return minorUnits === null ? null : clamp(minorUnits, 0, this.#limit);
    }
    #format(minorUnits) {
        return formatMoney(minorUnits, this.#fieldFormat, this.#currency);
    }
    #syncFromFields() {
        const low = this.#parse(this.#field('min')?.value ?? '') ?? 0;
        const high = this.#parse(this.#field('max')?.value ?? '') ?? this.#limit;
        this.#paint(low, high);
    }
    /** Takes the fields as they stand as the range the results already show. */
    #adopt() {
        this.#syncFromFields();
        this.#applied = this.#key();
    }
    /** The range in the fields, so that a commit that changes nothing can be told from one that does. */
    #key() {
        const low = this.#parse(this.#field('min')?.value ?? '') ?? 0;
        const high = this.#parse(this.#field('max')?.value ?? '') ?? this.#limit;
        return `${low}:${high}`;
    }
    /** Writes both bounds to the fields and the sliders. */
    #show(low, high) {
        const min = this.#field('min');
        const max = this.#field('max');
        if (min)
            min.value = low <= 0 ? '' : this.#format(low);
        if (max)
            max.value = high >= this.#limit ? '' : this.#format(high);
        this.#paint(low, high);
    }
    /** Moves the thumbs and the filled track, and says the values in words for assistive technology. */
    #paint(low, high) {
        const track = this.querySelector('[data-price-track]');
        const minRange = this.#range('min');
        const maxRange = this.#range('max');
        if (!track || !minRange || !maxRange)
            return;
        const enabled = this.#limit > 0;
        for (const range of [minRange, maxRange]) {
            range.min = '0';
            range.max = String(this.#ceiling);
            range.step = String(this.#step);
            range.disabled = !enabled;
        }
        const lowPosition = Math.min(low, this.#ceiling);
        const highPosition = high >= this.#limit ? this.#ceiling : Math.min(high, this.#ceiling);
        minRange.value = String(lowPosition);
        maxRange.value = String(highPosition);
        minRange.setAttribute('aria-valuetext', formatMoney(low, this.#fullFormat, this.#currency));
        maxRange.setAttribute('aria-valuetext', formatMoney(high, this.#fullFormat, this.#currency));
        const share = (position) => `${enabled ? (position / this.#ceiling) * 100 : 0}%`;
        track.style.setProperty('--facet-range-start', share(lowPosition));
        track.style.setProperty('--facet-range-end', enabled ? share(highPosition) : '100%');
        // Two thumbs at the far end overlap, so the lower one is lifted to stay reachable.
        track.dataset.raise = lowPosition > this.#ceiling / 2 ? 'min' : 'max';
        track.hidden = false;
    }
    #schedule() {
        this.#scheduled = true;
        this.#commit();
    }
    #request() {
        this.discardPending();
        this.#applied = this.#key();
        const form = this.closest('form');
        const facets = this.closest('facets-form-component');
        if (form && facets instanceof FacetsFormComponent)
            facets.updateFilters(form);
    }
    #onInput = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement))
            return;
        if (input.dataset.priceRange)
            this.#dragged(input.dataset.priceRange === 'min' ? 'min' : 'max');
        else if (input.dataset.priceField)
            this.#typed();
    };
    /** A thumb moved: the thumbs never cross, and the fields follow. */
    #dragged(kind) {
        const minRange = this.#range('min');
        const maxRange = this.#range('max');
        if (!minRange || !maxRange)
            return;
        let low = minRange.valueAsNumber;
        let high = maxRange.valueAsNumber;
        if (kind === 'min' && low > high)
            low = high;
        if (kind === 'max' && high < low)
            high = low;
        // The end of the track stands for no bound, which may lie above the highest price.
        this.#show(Math.min(low, this.#limit), high >= this.#ceiling ? this.#limit : Math.min(high, this.#limit));
        this.#schedule();
    }
    /** A field is being typed in: the thumbs follow, the text stays as typed. */
    #typed() {
        this.#syncFromFields();
        this.#schedule();
    }
    /** A field was committed: keep it inside the range and in the shop's format. */
    #onChange = (event) => {
        const input = event.target;
        if (input instanceof HTMLInputElement && input.dataset.priceField)
            this.#settled(input.dataset.priceField === 'min' ? 'min' : 'max');
    };
    #settled(edited) {
        let low = this.#parse(this.#field('min')?.value ?? '') ?? 0;
        let high = this.#parse(this.#field('max')?.value ?? '') ?? this.#limit;
        if (edited === 'min' && low > high)
            low = high;
        if (edited === 'max' && high < low)
            high = low;
        this.#show(low, high);
        if (this.#key() !== this.#applied)
            this.#schedule();
    }
    /** Enter in a field applies the range at once. */
    #onKeydown = (event) => {
        const input = event.target;
        if (event.key !== 'Enter' || !(input instanceof HTMLInputElement) || !input.dataset.priceField)
            return;
        event.preventDefault();
        this.#settled(input.dataset.priceField === 'min' ? 'min' : 'max');
        this.flush();
    };
    /** Money fields take digits, separators and spaces only. */
    #onBeforeInput = (event) => {
        if (!(event.target instanceof HTMLInputElement) || !event.target.dataset.priceField)
            return;
        if (event.data && !/^[\d.,'\s]+$/.test(event.data))
            event.preventDefault();
    };
    /**
     * A section morph must not overwrite digits the shopper is still typing. The morph keeps a field's
     * value when both its live and incoming copy carry this marker and the live one has focus.
     */
    #onFocusIn = (event) => {
        if (event.target instanceof HTMLInputElement && event.target.dataset.priceField)
            event.target.setAttribute('data-skip-value-update', 'true');
    };
    #onFocusOut = (event) => {
        if (event.target instanceof HTMLInputElement && event.target.dataset.priceField)
            event.target.removeAttribute('data-skip-value-update');
    };
}
if (!customElements.get('facets-form-component'))
    customElements.define('facets-form-component', FacetsFormComponent);
if (!customElements.get('price-facet-component'))
    customElements.define('price-facet-component', PriceFacetComponent);
